import type { MetricCanonicalName, MetricInfo, SetPlanInfoId } from '@az3fr/core';
import { memo, useCallback } from 'react';

import type { SelectOptionConfig } from '@/lib/option-generators';
import { setPlanSliceSelectors } from '@/entities/set-plan/model/set-plan.selector';
import { MetricInput } from '@/entities/set-plan/ui/metric-input';
import { workoutCreatorThunks } from '@/features/workout-creator/model';
import { useAppDispatch, useAppSelector } from '@/hooks/store.hook';

function MetricCellV2(props: {
	setPlanId: SetPlanInfoId;
	metricCanonicalName: MetricCanonicalName;
	metric: MetricInfo;
}) {
	const { setPlanId, metricCanonicalName, metric } = props;
	const dispatch = useAppDispatch();
	const setPlan = useAppSelector((state) => setPlanSliceSelectors.selectById(state, setPlanId));

	// This is where we convert the selected option to a number to update the redux store.
	const handleValueChange = useCallback(
		(selectedOption: SelectOptionConfig<number> | null) => {
			if (!selectedOption) {
				return;
			}
			const { value } = selectedOption;
			dispatch(
				workoutCreatorThunks.updateSetPlanTargetMetric({
					setPlanId,
					metricCanonicalName,
					value,
				}),
			);
		},
		[dispatch, setPlanId, metricCanonicalName],
	);

	if (!setPlan) {
		return null;
	}

	return (
		<MetricInput
			metric={metric}
			setPlan={setPlan}
			onValueChange={handleValueChange}
			buttonClassName="size-full cursor-pointer font-medium text-xs sm:text-sm focus:outline-none focus:ring-2 hover:bg-gray-50 focus:ring-gray-500 focus:ring-inset flex items-center justify-center p-2.5 sm:p-3"
		/>
	);
}

// Memoize to prevent re-renders when parent components update
const MemoizedMetricCellV2 = memo(MetricCellV2);

export { MemoizedMetricCellV2 as MetricCellV2 };
